'use client';
/**
 * Le bouton de clôture d'un dossier qualité, et le bandeau qui explique
 * pourquoi il ne part pas.
 *
 * ── Pourquoi il est ici, et pas dans chaque écran ───────────────────────────
 *
 * Trois registres portent la même exigence : on ne scelle pas un dossier dont
 * le traitement n'a pas eu lieu. La non-conformité l'appliquait depuis le
 * 27/07 ; l'audit et la réclamation, non — chacun avait sa version, plus courte.
 * Écrire la règle une fois est la seule façon qu'elle reste la même : trois
 * copies dérivent trois fois, et c'est la copie oubliée qu'un évaluateur ouvre.
 *
 * ── Ce qui fait qu'elle est VIVANTE ─────────────────────────────────────────
 *
 * Les obstacles sont passés en propriété, calculés par l'écran à CHAQUE rendu
 * depuis les actions et les NC du magasin. Aucun n'est stocké, aucun champ
 * « clôturable » n'existe en base. Une action terminée dans le panneau d'à côté
 * fait disparaître son obstacle au rendu suivant, sans que personne ait à
 * rafraîchir quoi que ce soit.
 *
 * Le serveur applique la MÊME règle et fait foi (`backend/src/api/ged/services/
 * cloture.ts`) : cet écran ne fait que l'expliquer avant le clic, et ouvrir au
 * RQ la dérogation motivée que le serveur accepte. Les deux doivent bouger
 * ensemble — le jour où ils divergeront, c'est le serveur qui aura raison, et
 * l'écran qui aura menti.
 */
import React, { useState } from 'react';
import { MOTIF_DEROGATION_MIN } from '@/lib/meta';
import { I, inputStyle, FieldError, ConfirmButton, useBusy } from '@/components/ui';
import { PointDeRegle } from '@/components/PointDeRegle';
import { useAutorisationResponsable, ChampsResponsable, type ResponsablePayload } from '@/components/AutorisationResponsable';

import { Bouton } from '@/shared/Bouton';
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5,
};

/** Un obstacle à la clôture. `onOuvrir` absent = obstacle qui ne désigne aucun
 *  enregistrement (« le réclamant n'a pas été informé ») : il s'affiche en
 *  texte, et non en bouton qui ne mènerait nulle part. */
export interface Blocage {
  /** Référence affichée (`BIO24-AC-2026-004`), ou '' s'il n'y en a pas. */
  ref: string;
  /** Ce qu'on lit à côté de la référence — « action en cours », « écart non déclaré ». */
  libelle: string;
  onOuvrir?: () => void;
  /** `false` : la dérogation RQ ne lève pas cet obstacle (rubrique du dossier
   *  restée vide). Le bouton de dérogation n'est alors pas proposé — le serveur
   *  refuserait, et l'offrir revenait à faire écrire un motif pour rien. */
  derogeable?: boolean;
}

export function BlocageCloture({
  objet, refDossier, blocages, estRQ, label, question, aide, ancreRegle, indiceNonRQ, onCloturer,
}: {
  /** Le dossier, en toutes lettres : « la non-conformité », « l'audit ». */
  objet: string;
  /** Sa référence, pour la question de confirmation. */
  refDossier: string;
  blocages: Blocage[];
  estRQ: boolean;
  label: string;
  question: string;
  /** L'explication normative — en infobulle, pas à l'écran : elle ne change
   *  jamais et se relirait à chaque ouverture de fiche. */
  aide: string;
  /** Le point de règle de CE registre (REG-2). Optionnel : les trois registres
   *  qui partagent ce composant ne clôturent pas sous les mêmes conditions, et
   *  celui qui n'a pas encore sa règle écrite n'affiche simplement pas de « i ».
   *  ⚠️ Ne pas y mettre une ancre commune : la clôture d'un audit et celle d'une
   *  non-conformité ne se refusent pas pour les mêmes raisons. */
  ancreRegle?: string;
  /** Conservé pour compatibilité de signature — n'est plus affiché : qui n'a
   *  pas le rôle RQ voit maintenant `ChampsResponsable` à la place
   *  (06/09/2026), pas un simple constat d'impuissance. */
  indiceNonRQ?: string;
  onCloturer: (motifDerogation?: string, responsable?: ResponsablePayload) => Promise<boolean> | boolean;
}) {
  const [derog, setDerog] = useState(false);
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  // Le RQ peut déroger depuis sa propre session ; sinon un responsable
  // s'authentifie pour ce seul geste, depuis le poste de qui clôture, sans
  // ouvrir de session (06/09/2026, `autoriserResponsable` côté serveur).
  const authResponsable = useAutorisationResponsable();
  // Obstacles que la dérogation ne lève pas : tant qu'il en reste un, on ne
  // propose pas d'écrire un motif que le serveur refusera de toute façon.
  const irremediables = blocages.filter((b) => b.derogeable === false);

  if (!blocages.length) {
    return (
      <ConfirmButton
        label={label} icon="shield" variant="primary"
        question={question} confirmer="Clôturer"
        onConfirm={() => onCloturer()} />
    );
  }

  return (
    <div style={{ flex: '1 1 100%', border: '1px solid var(--amber-300, #EFC77B)', background: 'var(--amber-50, #FFF8EC)', borderRadius: 'var(--radius-md)', padding: '9px 12px', display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span title={aide} style={{ display: 'inline-flex', cursor: 'help', color: 'var(--amber-600)' }}>
          <I n="shield" s={14} />
        </span>
        <strong style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>
          Clôture bloquée par {blocages.length} élément(s)
        </strong>
        {ancreRegle && <PointDeRegle ancre={ancreRegle} />}
        {blocages.map((b, i) => (b.onOuvrir ? (
          <Bouton taille="sm" key={b.ref || i} onClick={b.onOuvrir} title={`Ouvrir ${b.ref}`} style={{ fontFamily: 'var(--font-mono)' }}>
            {/* Un obstacle sans référence (une rubrique vide) n'a pas de code à
                afficher : le séparateur seul laissait un « · » orphelin. */}
            {b.ref ? `${b.ref} · ` : ''}{b.libelle} →
          </Bouton>
        ) : (
          <span key={b.ref || i} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--amber-700, #8A5B00)', background: '#fff', borderRadius: 'var(--radius-pill)', padding: '3px 9px' }}>
            {b.ref ? `${b.ref} · ` : ''}{b.libelle}
          </span>
        )))}
        <span style={{ flex: 1 }} />
        {/* ⚠️ UNE DÉROGATION N'EST PAS UNE ACTION SECONDAIRE (relevé du
            27/08/2026, P4c). Elle était présentée en petit bouton fantôme de
            11 px, au bout d'un bandeau d'alerte, comme une commande d'appoint.
            C'est pourtant l'acte le plus engageant de cet écran : il clôt un
            dossier EN SACHANT que les conditions ne sont pas réunies, il est
            nominatif et il part au journal signé — c'est exactement ce qu'un
            évaluateur vient lire.
            Elle prend donc le corps des autres actions (`Bouton` `sm`, 12,5 px, la
            hauteur de 30 px de la maison) et la couleur d'avertissement, qui
            dit ce qu'elle est sans crier comme une suppression. */}
        {!derog && !irremediables.length && (
          <Bouton taille="sm" onClick={() => { setDerog(true); setErreur(''); }}
            title="Clore le dossier alors que les conditions ne sont pas réunies. L’acte est nominatif, motivé, et inscrit au journal signé."
            style={{ borderColor: 'var(--amber-600)', color: 'var(--amber-700, #8A5B00)', background: 'var(--amber-50, #FCF2E1)', fontWeight: 700 }}>
            <I n="alert" s={13} />Clôturer par dérogation{estRQ ? ' (RQ)' : ''}
          </Bouton>
        )}
        {!!irremediables.length && (
          <span style={{ fontSize: 12.5, color: 'var(--amber-700, #8A5B00)' }}>
            {irremediables.length > 1
              ? 'Ces rubriques sont à renseigner — la dérogation ne les remplace pas.'
              : 'Cette rubrique est à renseigner — la dérogation ne la remplace pas.'}
          </span>
        )}
      </div>

      {derog && (
        <div style={{ display: 'grid', gap: 8 }}>
          <label style={lbl} htmlFor={`derog-${refDossier}`}>
            Motif de la dérogation <span style={{ color: 'var(--red-600)' }}>*</span>
          </label>
          <textarea id={`derog-${refDossier}`} value={motif} rows={2}
            onChange={(e) => { setMotif(e.target.value); if (erreur) setErreur(''); }}
            aria-invalid={!!erreur}
            placeholder="Ex. action devenue sans objet : l’automate concerné a été remplacé le 12/07."
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical', borderColor: erreur ? 'var(--red-600)' : undefined }} />
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {motif.trim().length}/{MOTIF_DEROGATION_MIN} caractères minimum — ce motif est inscrit
            dans l’événement signé et reste lisible en audit. Ce qui est listé ci-dessus n’est pas
            annulé et poursuit sa vie.
          </div>
          {/* Réservée au RQ — ou à un responsable qui s'authentifie ici même
              pour ce seul geste, sans ouvrir de session (06/09/2026). */}
          {!estRQ && <ChampsResponsable {...authResponsable} libelle="cette dérogation" />}
          <FieldError>{erreur}</FieldError>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Bouton variante="primaire" taille="sm" onClick={() => void run('derog', async () => {
                if (motif.trim().length < MOTIF_DEROGATION_MIN) {
                  setErreur(`Motif trop court : ${MOTIF_DEROGATION_MIN} caractères au minimum.`);
                  return;
                }
                const ok = await onCloturer(motif.trim(), authResponsable.payload);
                if (ok) { setDerog(false); setMotif(''); authResponsable.reinitialiser(); }
              })} disabled={estOccupe() || (!estRQ && !authResponsable.pret)}>
              {estOccupe('derog') ? 'Clôture…' : `Clôturer ${objet} sous dérogation`}
            </Bouton>
            <Bouton taille="sm" onClick={() => { setDerog(false); setMotif(''); setErreur(''); authResponsable.reinitialiser(); }}>
              Annuler
            </Bouton>
          </div>
        </div>
      )}
    </div>
  );
}
